/**
 * The sign-in screen's wiring: form state, the submit round-trip, and the navigation that follows.
 *
 * Every decision it needs is imported — the reducer from `model/signInForm`, the error mapping from
 * `model/authErrors`, and everything about the session from `entities/session`. What is left here is
 * the part that genuinely needs React and the router: holding the reducer, awaiting `signIn`, and
 * moving.
 *
 * No `useCallback` and no `useMemo`: the React Compiler is on (foundation ADR §1), so memoisation is
 * its job.
 */

import { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';

import {
  consumeSessionEndReason,
  readSession,
  routeForSessionStatus,
  signIn,
} from '~/entities/session';

import { resolveAuthErrorSurfaces } from '../model/authErrors';
import {
  INITIAL_SIGN_IN_FORM,
  canSubmitSignIn,
  signInFormReducer,
  type SignInFormState,
} from '../model/signInForm';

export interface SignInFormApi {
  state: SignInFormState;
  canSubmit: boolean;
  /**
   * The one message the banner shows. A failed attempt wins over the expired notice, because it is
   * the answer to what the trainer just did.
   */
  bannerMessage: string | null;
  setEmail: (value: string) => void;
  setPassword: (value: string) => void;
  submit: () => void;
}

export function useSignInForm(): SignInFormApi {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(signInFormReducer, INITIAL_SIGN_IN_FORM);

  // Read at first render, not subscribed to: the reason is consumed immediately below, so a
  // subscription would see it vanish and take the banner with it. Capturing it here is what makes
  // "shown exactly once" mean once per arrival rather than once per render.
  const [showExpiredNotice, setShowExpiredNotice] = useState(
    () => readSession().endReason === 'expired',
  );

  useEffect(() => {
    consumeSessionEndReason();
  }, []);

  function submit(): void {
    if (!canSubmitSignIn(state)) return;

    setShowExpiredNotice(false);
    dispatch({ type: 'SUBMIT_STARTED' });

    void (async () => {
      try {
        await signIn({ email: state.email, password: state.password });
      } catch (error) {
        dispatch({
          type: 'SUBMIT_FAILED',
          surfaces: resolveAuthErrorSurfaces(error, 'signIn', t),
        });
        return;
      }

      dispatch({ type: 'SUBMIT_SUCCEEDED' });

      // Where the trainer belongs now is the session's decision, not this screen's: the same answer
      // sends a refused account to the refusal screen and a profileless one to completion.
      const destination = routeForSessionStatus(readSession().status);
      if (destination !== null) void navigate({ to: destination, replace: true });
    })();
  }

  return {
    state,
    canSubmit: canSubmitSignIn(state),
    bannerMessage: state.errors.banner ?? (showExpiredNotice ? t('session.expired') : null),
    setEmail: (value) => dispatch({ type: 'EMAIL_EDITED', value }),
    setPassword: (value) => dispatch({ type: 'PASSWORD_EDITED', value }),
    submit,
  };
}
